import { useMemo, useState } from 'react'
import { Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Input, Skeleton, Switch } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useCanAccess } from 'hooks'
import { useAddAeoPrompt, usePatchAeoPrompt, useSeoAeo } from 'hooks/api'
import type { AeoResultView } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, dateTime } from '../shared'
import { fmt, fmtPct, fmtPos, fmtUsd } from './seoModel'

const verdict = (r: AeoResultView | undefined) => {
  if (!r) return '—'
  if (r.status !== 'ok') return r.status
  // A rate over the samples when a prompt was asked more than once this run.
  const frac = (k: number) => (r.answered > 1 ? ` ${k}/${r.answered}` : '')
  if (r.veraCited)
    return `Cited${frac(r.citedIn)}${r.veraPosition ? ` (best #${r.veraPosition})` : ''}`
  if (r.veraMentioned) return `Mentioned${frac(r.mentionedIn)}`
  return `Absent${frac(0)}`
}

/**
 * Answer-engine optimisation: when someone asks an AI assistant the questions our buyers
 * ask, is Vera in the answer? Two sources, labelled apart: Vera's own prompt runner (fixed
 * prompts, sent to each enabled engine on a cadence) and SE Ranking's AI search panel
 * (their own prompt set, off by default because it is expensive).
 */
export const SeoAeoCard = () => {
  const { data, isLoading, error } = useSeoAeo()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const addPrompt = useAddAeoPrompt()
  const patchPrompt = usePatchAeoPrompt()
  const [draft, setDraft] = useState('')

  const enabled = (data?.engines ?? []).filter((e) => e.enabled)
  const byPrompt = useMemo(() => {
    const m = new Map<string, Map<string, AeoResultView>>()
    for (const r of data?.latest ?? []) {
      const inner = m.get(r.promptId) ?? new Map<string, AeoResultView>()
      inner.set(r.engine, r)
      m.set(r.promptId, inner)
    }
    return m
  }, [data])

  if (isLoading) {
    return (
      <Card title="AI answers">
        <Stack sx={{ mt: 4 }}>
          <Skeleton height={14} lines={10} />
        </Stack>
      </Card>
    )
  }
  if (error || !data) {
    return (
      <Card title="AI answers">
        <Stack sx={{ mt: 4 }}>
          <SectionNote>Could not load AI answers: {(error as Error | null)?.message}</SectionNote>
        </Stack>
      </Card>
    )
  }

  return (
    <Stack spacing={6}>
      <Card title="AI answers">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Engine</HeadCell>
                <HeadCell>Model</HeadCell>
                <HeadCell>State</HeadCell>
                <HeadCell>Last run</HeadCell>
                <HeadCell align="right">Answers</HeadCell>
                <HeadCell align="right">Searched the web</HeadCell>
                <HeadCell align="right">
                  Vera cited
                  <InfoTooltip term="aeo-citation" />
                </HeadCell>
                <HeadCell align="right">Vera mentioned</HeadCell>
                <HeadCell>Most-cited competitors (links)</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.engines.map((e) => {
                const s = data.summary.find((x) => x.engine === e.id)
                return (
                  <TableRow key={e.id}>
                    <Cell strong>{e.id}</Cell>
                    <Cell>{e.model}</Cell>
                    <Cell>
                      {e.enabled ? 'On' : `Off${e.disabledReason ? `: ${e.disabledReason}` : ''}`}
                    </Cell>
                    <Cell>{s?.runAt ? dateTime(s.runAt) : 'never'}</Cell>
                    <Cell align="right">
                      {s
                        ? `${fmt(s.answered)}${
                            s.samplesPerPrompt > 1
                              ? ` (${s.prompts} × ${s.samplesPerPrompt})`
                              : ''
                          }`
                        : '—'}
                    </Cell>
                    <Cell align="right">
                      {s && s.answered ? fmtPct(s.searched / s.answered, 0) : '—'}
                    </Cell>
                    <Cell align="right">
                      {s && s.answered ? fmtPct(s.veraCited / s.answered, 0) : '—'}
                    </Cell>
                    <Cell align="right">
                      {s && s.answered ? fmtPct(s.veraMentioned / s.answered, 0) : '—'}
                    </Cell>
                    <Cell>
                      {s && s.competitors.length
                        ? s.competitors
                            .slice(0, 3)
                            .map((c) => `${c.domain} ${c.cited}`)
                            .join(', ')
                        : '—'}
                    </Cell>
                  </TableRow>
                )
              })}
            </TableBody>
          </StatTable>
          <SectionNote>
            Each run asks every active prompt below several times (one answer is one sample, so
            each cell is a rate over the samples), with web search on, and records whether
            vera.inc is cited as a source or Vera is named in the text. &ldquo;Searched the
            web&rdquo; is how often the engine actually used search: an answer from memory reflects
            training data, not this week&rsquo;s results. A bare &ldquo;Vera&rdquo; counts only beside
            what Vera does (another Vera, like Vera Bradley, does not). Spend counts against
            the {fmtUsd(data.costCapUsd)} monthly cap shown under Jobs and spend.
          </SectionNote>
          {enabled.length === 0 ? (
            <Notice severity="info">
              No engine is enabled on this API, so the prompts below have not been asked anywhere
              yet. Each engine switches on when its credentials are configured.
            </Notice>
          ) : null}
        </Stack>
      </Card>

      <Card title="Prompts">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ md: 'flex-end' }}
          >
            <Input
              label="Add a prompt"
              placeholder="What is the best way to prove a luxury handbag is authentic?"
              value={draft}
              onChange={(v: string) => setDraft(v)}
              disabled={!isAdmin}
            />
            <Button
              variant="secondary"
              disabled={!isAdmin || draft.trim().length < 8 || addPrompt.isPending}
              disabledReason={
                !isAdmin ? 'Only platform admins can edit the prompt set.' : undefined
              }
              loading={addPrompt.isPending}
              onClick={() =>
                addPrompt.mutate(
                  { prompt: draft.trim(), topic: null },
                  { onSuccess: () => setDraft('') },
                )
              }
            >
              Add prompt
            </Button>
          </Stack>
          {addPrompt.error ? (
            <Notice severity="error">
              {(addPrompt.error as Error).message}
            </Notice>
          ) : null}
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Prompt</HeadCell>
                <HeadCell>Topic</HeadCell>
                {data.engines.map((e) => (
                  <HeadCell key={e.id}>{e.id}</HeadCell>
                ))}
                <HeadCell>Active</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.prompts.length === 0 ? (
                <EmptyRow colSpan={3 + data.engines.length} text="No prompts yet." />
              ) : (
                data.prompts.map((p) => (
                  <TableRow key={p.id}>
                    <Cell strong>
                      <Typography
                        variant="body3"
                        sx={{
                          whiteSpace: 'normal',
                          maxWidth: 420,
                          display: 'block',
                        }}
                      >
                        {p.prompt}
                      </Typography>
                    </Cell>
                    <Cell>{p.topic ?? '—'}</Cell>
                    {data.engines.map((e) => {
                      const r = byPrompt.get(p.id)?.get(e.id)
                      return (
                        <Cell key={e.id}>
                          {r?.excerpt || r?.error ? (
                            <InfoTooltip term={r.excerpt ? 'aeo-citation' : undefined} text={r.excerpt ?? r.error}>
                              <span style={{ cursor: 'help' }}>{verdict(r)}</span>
                            </InfoTooltip>
                          ) : (
                            verdict(r)
                          )}
                        </Cell>
                      )
                    })}
                    <Cell>
                      <Switch
                        checked={p.active}
                        disabled={!isAdmin || patchPrompt.isPending}
                        inputProps={{
                          'aria-label': `Prompt active: ${p.prompt}`,
                        }}
                        onChange={(_, checked) => patchPrompt.mutate({ id: p.id, active: checked })}
                      />
                    </Cell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </StatTable>
        </Stack>
      </Card>

      <Card title="SE Ranking AI search">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <SectionNote>
            SE Ranking&rsquo;s own view of brand presence in AI answers, over their prompt set
            rather than ours. Off by default: an overview costs{' '}
            {fmt(data.aiSearchCosts.overviewPerRequest)} credits a request and a leaderboard{' '}
            {fmt(data.aiSearchCosts.leaderboardPerRequest)}.
          </SectionNote>
          <Notice severity="warning">
            Read link presence (a cited URL) first. Brand mentions are a string match on the name
            and may include homonyms: the 2026-10-01 leaderboard credited &ldquo;Vera&rdquo; with 3,094
            mentions and zero links, and EON with 873 mentions against 10 links (E.ON, the energy
            company). Share of voice pools both, so it inherits the noise.
          </Notice>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Report</HeadCell>
                <HeadCell>Market</HeadCell>
                <HeadCell>Engine</HeadCell>
                <HeadCell>Domain</HeadCell>
                <HeadCell align="right">Link presence</HeadCell>
                <HeadCell align="right">Brand mentions</HeadCell>
                <HeadCell align="right">Avg position</HeadCell>
                <HeadCell align="right">Share of voice (as reported)</HeadCell>
                <HeadCell>Fetched</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.aiSearch.length === 0 ? (
                <EmptyRow colSpan={9} text="No AI search snapshots yet." />
              ) : (
                data.aiSearch.map((s) => (
                  <TableRow
                    key={`${s.endpoint}-${s.market}-${s.engine}-${s.target}-${s.fetchedAt}`}
                  >
                    <Cell>{s.endpoint}</Cell>
                    <Cell>{s.market}</Cell>
                    <Cell>{s.engine ?? 'all'}</Cell>
                    <Cell strong>{s.target}</Cell>
                    <Cell align="right" strong>
                      {fmt(s.linkPresence)}
                    </Cell>
                    <Cell align="right">{fmt(s.brandPresence)}</Cell>
                    <Cell align="right">{fmtPos(s.averagePosition)}</Cell>
                    <Cell align="right">{fmt(s.shareOfVoice, 1)}</Cell>
                    <Cell>{dateTime(s.fetchedAt)}</Cell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </StatTable>
        </Stack>
      </Card>
    </Stack>
  )
}
